/**
 * @component ry-chat
 * @category  display
 * @summary   Conversation wrapper for a list of `<ry-chat-bubble>` messages. Pure flex column with a default gap; sets no other semantics. Maps to daisyUI `.chat` (the container). Ported MIT from daisyUI — see LICENSE-THIRD-PARTY.md.
 *
 * Reference: daisyUI `chat` component (https://daisyui.com/components/chat/).
 * The container is a flex column whose direct children are usually
 * `<ry-chat-bubble>` elements. The bubble owns the placement / variant / avatar
 * surface; `<ry-chat>` is the rhythm/spacing scaffold around them.
 *
 * @content   One or more `<ry-chat-bubble>` children (typed messages). Any other
 *            HTML is permitted — the wrapper makes no assumptions.
 *
 * @gotcha    `<ry-chat>` does NOT set ARIA role. If you need announce-on-update
 *            behavior for a live conversation, wrap it in `<div role="log"
 *            aria-live="polite">` or set the attributes on `<ry-chat>` yourself.
 *
 * @example basic
 * ```html
 * <ry-chat>
 *   <ry-chat-bubble placement="start">Hey, did you see the demo?</ry-chat-bubble>
 *   <ry-chat-bubble placement="end" variant="primary">Yes! Looks great.</ry-chat-bubble>
 *   <ry-chat-bubble placement="start">Want to ship it?</ry-chat-bubble>
 *   <ry-chat-bubble placement="end" variant="primary">Already on it.</ry-chat-bubble>
 * </ry-chat>
 * ```
 *
 * @example with-avatars
 * ```html
 * <ry-chat>
 *   <ry-chat-bubble placement="start" avatar="https://i.pravatar.cc/64?img=11" name="Anakin" time="12:45">
 *     Are you sure about this plan?
 *   </ry-chat-bubble>
 *   <ry-chat-bubble placement="end" variant="primary" avatar="https://i.pravatar.cc/64?img=12" name="Obi-Wan" time="12:46" footer="Seen at 12:46">
 *     I have the high ground.
 *   </ry-chat-bubble>
 * </ry-chat>
 * ```
 *
 * @tutorial wraps-children-with-gap
 * ```js
 * const el = mount(`
 *   <ry-chat>
 *     <ry-chat-bubble>a</ry-chat-bubble>
 *     <ry-chat-bubble>b</ry-chat-bubble>
 *   </ry-chat>
 * `);
 * await expectEqual(el.children.length, 2, 'children are preserved verbatim');
 * await expectEqual(el.tagName, 'RY-CHAT');
 * ```
 *
 * @see ry-chat-bubble
 */

/**
 * @component ry-chat-bubble
 * @category  display
 * @summary   A single message in a chat conversation — avatar, name, time, body bubble (with tail pointing at the avatar), and an optional footer. Ported MIT from daisyUI's `.chat-bubble` — see LICENSE-THIRD-PARTY.md.
 *
 * @attribute placement   'start' | 'end'                                              default='start'   Which side of the conversation the bubble sits on. `start` aligns left with the tail pointing left toward an avatar on the left; `end` mirrors to the right.
 * @attribute variant     'default'|'primary'|'secondary'|'accent'|'info'|'success'|'warning'|'danger'   default='default'   Bubble color family. `default` uses neutral surface tokens; the others use semantic palette tokens.
 * @attribute avatar      string?                                                                          Avatar image URL. When set, renders a circular `<img>` in the avatar cell with `alt` matching `name`.
 * @attribute name        string?                                                                          Author name shown in the small header row above the bubble.
 * @attribute time        string?                                                                          Timestamp shown next to the name in the header row (free text — pre-formatted by the caller).
 * @attribute footer      string?                                                                          Small text shown below the bubble (e.g. "Delivered", "Seen at 12:46").
 *
 * @content   The element's textContent (or innerHTML) becomes the bubble body. Setup wraps the original markup inside the bubble cell — block elements are preserved.
 *
 * @dom
 *   ry-chat-bubble[placement="start"|"end"][variant][avatar?][name?][time?][footer?]
 *     > div.ry-chat-bubble__avatar  [data-ry-target="avatar"]   (only when [avatar])
 *       > img src=avatar alt=name
 *     > div.ry-chat-bubble__header  [data-ry-target="header"]   (only when [name] or [time])
 *       > span.ry-chat-bubble__name [data-ry-target="name"]     (only when [name])
 *       > span.ry-chat-bubble__time [data-ry-target="time"]     (only when [time])
 *     > div.ry-chat-bubble__bubble  [data-ry-target="bubble"]   {bubble body — tail pseudo-element points at the avatar}
 *     > div.ry-chat-bubble__footer  [data-ry-target="footer"]   (only when [footer])
 *
 * @gotcha    `placement` is reflected as a `data-ry-placement` attribute on the
 *            host AND a fallback class (`.ry-chat-bubble--start` / `--end`).
 *            Both hooks exist so structural CSS (FORM) and theme CSS (THEME)
 *            can target either layer without coupling.
 *
 * @gotcha    Setup replaces innerHTML — any nested DOM in the original element
 *            is moved into the bubble cell. Mount the bubble once with its
 *            children present (don't try to re-`innerHTML =` an already-upgraded
 *            empty `<ry-chat-bubble>` and expect it to re-run setup; the
 *            archetype-B "setup-once-read-children" rule applies).
 *
 * @gotcha    No `variant="neutral"` — use `default` (the absent / empty variant)
 *            for the neutral surface color. daisyUI's `chat-bubble-neutral`
 *            collapses into ry-ui's default.
 *
 * @example default-alternating
 * ```html
 * <ry-chat>
 *   <ry-chat-bubble placement="start">Ping</ry-chat-bubble>
 *   <ry-chat-bubble placement="end">Pong</ry-chat-bubble>
 * </ry-chat>
 * ```
 *
 * @example with-avatar-name-time
 * ```html
 * <ry-chat>
 *   <ry-chat-bubble placement="start" avatar="https://i.pravatar.cc/64?img=11" name="Anakin" time="12:45">
 *     You underestimate my power.
 *   </ry-chat-bubble>
 * </ry-chat>
 * ```
 *
 * @example variants
 * ```html
 * <ry-chat>
 *   <ry-chat-bubble placement="end" variant="primary">Primary</ry-chat-bubble>
 *   <ry-chat-bubble placement="end" variant="secondary">Secondary</ry-chat-bubble>
 *   <ry-chat-bubble placement="end" variant="accent">Accent</ry-chat-bubble>
 *   <ry-chat-bubble placement="end" variant="info">Info</ry-chat-bubble>
 *   <ry-chat-bubble placement="end" variant="success">Success</ry-chat-bubble>
 *   <ry-chat-bubble placement="end" variant="warning">Warning</ry-chat-bubble>
 *   <ry-chat-bubble placement="end" variant="danger">Danger</ry-chat-bubble>
 * </ry-chat>
 * ```
 *
 * @example with-footer
 * ```html
 * <ry-chat-bubble placement="end" variant="primary" footer="Seen at 12:46">
 *   On my way.
 * </ry-chat-bubble>
 * ```
 *
 * @tutorial body-is-lifted-into-bubble-cell
 * ```js
 * const el = mount('<ry-chat-bubble>hello</ry-chat-bubble>');
 * const bubble = el.querySelector('[data-ry-target="bubble"]');
 * await expectEqual(bubble.textContent.trim(), 'hello', 'body lifted into bubble');
 * ```
 *
 * @tutorial placement-default-start
 * ```js
 * // Wrong assumption: an absent placement attribute leaves no signal.
 * // It defaults to 'start' (reflected as data-ry-placement="start").
 * const el = mount('<ry-chat-bubble>x</ry-chat-bubble>');
 * await expectAttr(el, 'data-ry-placement', 'start');
 * ```
 *
 * @tutorial placement-end
 * ```js
 * const el = mount('<ry-chat-bubble placement="end">x</ry-chat-bubble>');
 * await expectAttr(el, 'data-ry-placement', 'end');
 * ```
 *
 * @tutorial variant-reflected
 * ```js
 * const el = mount('<ry-chat-bubble variant="primary">x</ry-chat-bubble>');
 * await expectAttr(el, 'data-ry-variant', 'primary');
 * ```
 *
 * @tutorial avatar-renders-with-alt-as-name
 * ```js
 * const el = mount('<ry-chat-bubble avatar="/a.jpg" name="Jane">x</ry-chat-bubble>');
 * const slot = el.querySelector('[data-ry-target="avatar"]');
 * await expectEqual(!!slot, true);
 * const img = slot.querySelector('img');
 * await expectAttr(img, 'src', '/a.jpg');
 * await expectAttr(img, 'alt', 'Jane');
 * ```
 *
 * @tutorial header-only-when-name-or-time
 * ```js
 * const a = mount('<ry-chat-bubble>x</ry-chat-bubble>');
 * await expectEqual(a.querySelector('[data-ry-target="header"]'), null, 'no header when neither name nor time');
 * const b = mount('<ry-chat-bubble name="X">x</ry-chat-bubble>');
 * await expectEqual(!!b.querySelector('[data-ry-target="header"]'), true, 'header when name');
 * const c = mount('<ry-chat-bubble time="12:00">x</ry-chat-bubble>');
 * await expectEqual(!!c.querySelector('[data-ry-target="header"]'), true, 'header when time');
 * ```
 *
 * @tutorial footer-only-when-set
 * ```js
 * const a = mount('<ry-chat-bubble>x</ry-chat-bubble>');
 * await expectEqual(a.querySelector('[data-ry-target="footer"]'), null);
 * const b = mount('<ry-chat-bubble footer="Seen">x</ry-chat-bubble>');
 * await expectEqual(b.querySelector('[data-ry-target="footer"]').textContent, 'Seen');
 * ```
 *
 * @see ry-chat
 */

import { RyElement } from '../core/ry-element.js';

export class RyChat extends RyElement {
  setup(): void {
    // Pure flex-column wrapper — no markup transform needed.
  }
}

const VARIANTS = new Set(['default', 'primary', 'secondary', 'accent', 'info', 'success', 'warning', 'danger']);
const PLACEMENTS = new Set(['start', 'end']);

export class RyChatBubble extends RyElement {
  setup(): void {
    this.#transformMarkup();
  }

  #transformMarkup(): void {
    if (this.$('[data-ry-target="bubble"]')) return;

    const placementAttr = this.getAttribute('placement') ?? 'start';
    const placement = PLACEMENTS.has(placementAttr) ? placementAttr : 'start';

    const variantAttr = this.getAttribute('variant') ?? 'default';
    const variant = VARIANTS.has(variantAttr) ? variantAttr : 'default';

    const avatar = this.getAttribute('avatar') ?? '';
    const name = this.getAttribute('name') ?? '';
    const time = this.getAttribute('time') ?? '';
    const footer = this.getAttribute('footer') ?? '';

    // Capture original inner content (HTML, not just text) so block elements
    // and inline markup survive the transform.
    const body = this.innerHTML.trim();

    // Reflect placement + variant onto the host for both structure and theme
    // CSS hooks. data-ry-* is the canonical hook; the class is the theme hook.
    this.setAttribute('data-ry-placement', placement);
    this.setAttribute('data-ry-variant', variant);
    this.classList.add('ry-chat-bubble', `ry-chat-bubble--${placement}`, `ry-chat-bubble--variant-${variant}`);

    const avatarHTML = avatar
      ? `<div data-ry-target="avatar" class="ry-chat-bubble__avatar"><img src="${avatar}" alt="${name}"></div>`
      : '';

    const headerHTML = (name || time)
      ? `<div data-ry-target="header" class="ry-chat-bubble__header">${
          name ? `<span data-ry-target="name" class="ry-chat-bubble__name">${name}</span>` : ''
        }${
          time ? `<span data-ry-target="time" class="ry-chat-bubble__time">${time}</span>` : ''
        }</div>`
      : '';

    const footerHTML = footer
      ? `<div data-ry-target="footer" class="ry-chat-bubble__footer">${footer}</div>`
      : '';

    this.innerHTML = `
      ${avatarHTML}
      ${headerHTML}
      <div data-ry-target="bubble" class="ry-chat-bubble__bubble">${body}</div>
      ${footerHTML}
    `;
  }
}

customElements.define('ry-chat', RyChat);
customElements.define('ry-chat-bubble', RyChatBubble);
